<template>
  <div>
    <div id="wrapper">
      <div class="lectureWrap" :class="{on : learningStore.isHideBarsToggle}">
        <GlobalLoading v-if="loadingStore.show"/>
        <template v-else>
          <LazyLearningContestHeader />
          <LazyLearningMiddle />
          <LazyLearningContestFooter />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  LazyLearningContestFooter,
  LazyLearningContestHeader,
  LazyLearningMiddle
} from "#components";
import { computed, onBeforeMount, onMounted, ref, useRoute, useRouter, watchEffect } from "#imports";
import { useNuxtApp } from "#app";
import { useLearningStore } from "~/store/learning/learningStore";
import {useLearningAssessmentStore} from "~/store/learning/assessment/learningAssessmentStore";
import { onBeforeRouteLeave } from "vue-router";
import { useUserStore } from "~/store/user/userStore";
import { useContestService } from "~/composables/service/useContestService";
import DialogType from "~/components/Common/Dialog/model/DialogType";
import { useDialogStore } from "~/store/dialog/dialogStore";
import { useGenericDialog } from "~/composables/useGenericDialog";
import { useToastStore } from "~~/application/store/toast/toastStore";
import { useI18n } from "vue-i18n";
import { useLearningWindowEventService } from "~~/application/composables/service/useLearningWindowEventService";
import type ContestResponse from "~~/package/apis/contest/response/ContestResponse";
import {
  useLearningScreenRecordingService
} from "~~/application/composables/service/useLearningScreenRecordingService";
import { onBeforeUnmount } from "vue";
import { RecorderDomainType } from "~~/package/mapper/recorder/model/RecorderDomainType";
import { useLoadingStore } from "~/store/loading/loadingStore";
import {TaskType} from "~~/package/mapper/learning/model/TaskType";
import {ToastType} from "~/components/Common/Toast/model/ToastType";
import { useTaskDurationTrackingService } from "~/composables/service/useTaskDurationTrackingService";

// TSK-11215: no sessionOwner meta here on purpose. This is not a SkillCertify assessment — the
// termination popup's copy and i18n keys (SkillCertify-duplicate-session-warning-*) are
// SkillCertify-specific, and this route has no assessmentId param, so the popup's 확인 button
// would send the user to "/" instead of back to what they were doing.

const { getContestTasksWithLatestCodes, setCurrentContestTask } = useContestService();
const { openWrongApproachAlert } = useGenericDialog();
const learningStore = useLearningStore();
    const assessmentStore = useLearningAssessmentStore();
const loadingStore = useLoadingStore();
const learningWindowEventService = useLearningWindowEventService(RecorderDomainType.CONTEST);
const learningScreenRecordingService = useLearningScreenRecordingService();
const dialogStore = useDialogStore();
const userStore = useUserStore();
const toastStore = useToastStore();
const router = useRouter();
const route = useRoute();
const contestData = ref<ContestResponse | null>(null);
const taskDurationTrackingService = useTaskDurationTrackingService();

const { $i18n } = useNuxtApp();
const lang = computed(() =>
  $i18n.locale.value.toUpperCase()
);
const { t } = useI18n();

watchEffect(() => {
  if (learningStore.isLastTask) methods.openCompletePopup();
});

onBeforeRouteLeave((to, from, next) => {
  learningStore.resetAll();
  learningScreenRecordingService.stopRecording();
  assessmentStore.cautionFlag.multiMonitor = true;
  dialogStore.clearMultiOption();
  next();
});

onBeforeMount(() => {
  loadingStore.openLoading("");
  learningStore.resetMultiFileLearning();
  learningStore.resetAssessment();
  learningStore.resetLevelAssessment();
  learningStore.resetProSkillAssure();
});

onMounted(() => {
  learningStore.isNeverRun = true;
  learningStore.isChangedButNotSubmit = false;
  if (userStore.isLogin()) {
    learningStore.resetInitData();
    methods.initContestData();
  } else methods.goToHomePage();
});

// task 변경 시 duration tracking 업데이트
watch(() => route.params.taskId, (newTaskId, oldTaskId) => {
  if (newTaskId && oldTaskId && newTaskId !== oldTaskId) {
    const newTaskIdNum = parseInt(newTaskId as string);
    
    // 이전 task의 duration tracking 중지
    taskDurationTrackingService.stopTracking();
    
    // 새 task로 duration tracking 시작
    if (assessmentStore.contestList.some(task => task.id === newTaskIdNum)) {
      taskDurationTrackingService.startTracking(newTaskIdNum);
    }
  }
});

onBeforeUnmount(() => {
  learningWindowEventService.removeEventListener(
    false,
    assessmentStore.proctoringConfig.useShortcutProctor,
    assessmentStore.proctoringConfig.useCopyPasteProctor,
    assessmentStore.proctoringConfig.useTabSwitchProctor,
    assessmentStore.proctoringConfig.useMultiMonitorProctor
  );
  
  // task duration tracking 정리
  taskDurationTrackingService.stopTracking();
})

const initAntiCheating = () => {
  learningWindowEventService.addEventListener(
    false,
    assessmentStore.proctoringConfig.useShortcutProctor,
    assessmentStore.proctoringConfig.useCopyPasteProctor,
    assessmentStore.proctoringConfig.useTabSwitchProctor,
    assessmentStore.proctoringConfig.useMultiMonitorProctor
  );
};

const initScreenRecording = async () => {
  return await learningScreenRecordingService.startRecording(RecorderDomainType.CONTEST);
};

watch(() => learningStore.isSaved, () => {
  if (learningStore.task.type === TaskType.CODE_WITHOUT_RUN && learningStore.isSaved !== null) {
    if (learningStore.isSaved)
      toastStore.openToast({
        toastType: ToastType.SUCCESS,
        contents: t("learning-saved"),
      });
    else {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: t("learning-save-failed"),
      });
    }

    setTimeout(() => {
      learningStore.isSaved = null;
      toastStore.closeToast();
    }, 2000);
  }
});

const methods = {
  initContestData() {
    this.getContestData()
      .then(() => this.initTask())
      .catch(() => {
        methods.openFailPopup();
      });
  },
  async getContestData() {
    if(assessmentStore.contestId === parseInt(route.params.contestId as string) && assessmentStore.contestList.length > 0) {
      return;
    }
    contestData.value = await getContestTasksWithLatestCodes(parseInt(route.params.contestId as string), lang.value);
    if (contestData.value.contestTaskList.length < 1) methods.openFailPopup();
    assessmentStore.contestId = parseInt(route.params.contestId as string);
    assessmentStore.contestTitle = contestData.value.title;
    assessmentStore.contestList = contestData.value.contestTaskList;
    assessmentStore.contestEndedAt = contestData.value.challengeEndedAt;
    assessmentStore.proctoringConfig.useMultiMonitorProctor = contestData.value.useMultiMonitorProctor;
    assessmentStore.proctoringConfig.useCopyPasteProctor = contestData.value.useCopyPasteProctor;
    assessmentStore.proctoringConfig.useShortcutProctor = contestData.value.useShortcutProctor;
    assessmentStore.proctoringConfig.useTabSwitchProctor = contestData.value.useTabSwitchProctor;
    assessmentStore.proctoringConfig.useScreenRecording = contestData.value.useScreenRecording;
    assessmentStore.proctoringConfig.agreeOnScreenRecording = contestData.value.agreeOnScreenRecording;
  },
  goToHomePage() {
    router.push({ path: "/" });
  },
  goToParamTask() {
    let taskId = parseInt(route.params.taskId as string);

    if (!assessmentStore.contestList.some(task => task.id === taskId)) {
      taskId = assessmentStore.contestList[0].id;
      router.replace({ params: { ...route.params, taskId } });
    }
  },
  async initTask() {
    this.goToParamTask();

    const contestTask = assessmentStore.contestList.find(
      contestTask => contestTask.id === parseInt(route.params.taskId as string)
    );

    if (!contestTask) {
      console.error('Contest task not found');
      return;
    }

    learningStore.updateLearningData({ ...contestTask });
    assessmentStore.taskTestTemplateId = contestTask.taskTestTemplateId;

    // task duration tracking 시작
    if (contestTask.id) {
      taskDurationTrackingService.startTracking(contestTask.id);
    }

    await setCurrentContestTask(assessmentStore.contestId, contestTask.id)
      .then(isSuccess => {
        if (isSuccess) loadingStore.closeLoading();
      });

    if (assessmentStore.proctoringConfig.useScreenRecording && assessmentStore.recorderStream === null) {
      const recodingSuccess = await initScreenRecording();
      if (recodingSuccess) initAntiCheating();
    } else {
      initAntiCheating();
    }
  },
  openFailPopup() {
    openWrongApproachAlert();
  },
  openCompletePopup() {
    const isAllComplete = assessmentStore.contestList.every(contest => contest.submissionFlag);
    if (isAllComplete) {
      dialogStore.open({
        type: DialogType.LearningConfirmPopup,
        props: {
          icon: "finish",
          title: t("challenges-finish-challenge-title"),
          content: t("skillCertify-assessment-finish-question-description-success-submitted") + "\n" +
              t('skillCertify-assessment-finish-question-description-check'),
          isAllComplete,
        },
        confirm: {
          title: t("skillCertify-assessment-finish-question-button"),
          event: methods.onClickFinish
        },
        close: {
          title: t("common-cancel"),
          event: () => {
            learningStore.isLastTask = false;
          }
        }
      });
    }
    else {
      dialogStore.open({
        type: DialogType.LearningConfirmPopup,
        props: {
          icon: "finish",
          title: t("challenges-finish-challenge-title"),
          content: `<span class="red">${t('skillCertify-assessment-finish-question-description-not-submitted')}</span>`,
          isAllComplete
        },
        confirm: {
          title: t("skillCertify-assessment-finish-question-button"),
          event: methods.onClickFinish
        },
        close: {
          title: t("common-cancel"),
          event: () => {
            learningStore.isLastTask = false;
          }
        }
      });
    }
    learningStore.isLastTask = false;
  },
  onClickFinish() {
    router.push({path: `/dashboard/contest/${assessmentStore.contestId}`, query: {type: 'leader_board'}});
  }
};
</script>
